import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import LabelRow from "@components/rowcomponents/LabelRow";
import { primeServices } from "@services/apis/Prime";
import {
  GetPrimeTradeDetailsData,
  GetPrimeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeTradeDetailsModel";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ObjectKeyValueRows, {
  isPlainObject,
} from "./components/ObjectKeyValueRows";

interface PrimeUserDetailsProps {
  tradeId: string;
}

function toDetailsObject(value: unknown): Record<string, unknown> | null {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return isPlainObject(parsed) ? parsed : { value: parsed };
    } catch {
      return { value };
    }
  }

  if (isPlainObject(value)) {
    return value;
  }

  return { value };
}

const PrimeUserDetails: React.FC<PrimeUserDetailsProps> = ({ tradeId }) => {
  const [data, setData] = useState<GetPrimeTradeDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeTradeDetailsResponse =
        await primeServices.getPrimeTradeDetails({ tradeId: tradeId });
      setData(response.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tradeId]);

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return (
      <div className="border border-border-subtle rounded-md bg-bg-base mt-[20px]">
        <Skeleton height={100} />
      </div>
    );
  }

  const userDetails = toDetailsObject(data?.merchantUserDetails);
  const showPixDetails = data?.countryCode === "BR";

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px] overflow-auto"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          User Details:
        </p>
        {userDetails ? (
          <ObjectKeyValueRows data={userDetails} includeRootRaw />
        ) : (
          <span className="text-text-base text-[12px] font-normal">
            No data available
          </span>
        )}
      </div>

      {showPixDetails && (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            PIX Details:
          </p>
          <LabelRow
            label="Auto Settled?"
            valueComp={
              <DataPill value={data?.isPixAutoSettle === "1" ? "yes" : "no"} />
            }
          />
          <LabelRow
            label="CPF Key"
            value={data?.brlPixDetails?.cpfKey ?? ""}
            isCopy={!!data?.brlPixDetails?.cpfKey}
          />
          <LabelRow
            label="Pix Key"
            value={data?.brlPixDetails?.pixKey ?? ""}
            isCopy={!!data?.brlPixDetails?.pixKey}
          />
          <LabelRow
            label="Full Name"
            value={data?.brlPixDetails?.fullName ?? ""}
            isCopy={!!data?.brlPixDetails?.fullName}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal break-words">
                  {data?.brlPixDetails?.fullName || "- -"}
                </span>
                {data?.brlPixDetails?.fullName && (
                  <CopyButton
                    copyText={data.brlPixDetails.fullName}
                    copyMessage="Copied to clipboard"
                  />
                )}
              </div>
            }
          />
          <LabelRow
            label="Pix Key Type"
            value={data?.brlPixDetails?.pixKeyType ?? ""}
            isCopy={!!data?.brlPixDetails?.pixKeyType}
          />
        </div>
      )}
    </div>
  );
};

export default PrimeUserDetails;
